import React, { useContext, useEffect, useState } from 'react';
import { Navigate, Link, useNavigate } from 'react-router-dom';
import AuthContext from '../contexts/AuthContext';
import RoleBasedSidebar from '../components/RoleBasedSidebar';
import api from '../api/api';
import '../styles/DashboardPage.css';

// Role-specific dashboard components
import StudentDashboard from './dashboards/StudentDashboard';
import TeacherDashboard from './dashboards/TeacherDashboard';
import ParentDashboard from './dashboards/ParentDashboard';
import AdminDashboard from './dashboards/AdminDashboard';
import SuperAdminDashboard from './dashboards/SuperAdminDashboard';

const DashboardPage = () => {
  const { user, loading, isAuthenticated, logout } = useContext(AuthContext);
  const [dashboardData, setDashboardData] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = window.location.pathname;
  const navigate = useNavigate();

  // Direct navigation handler using React Router's navigate
  const handleNavLinkClick = (path) => {
    // Close the sidebar first
    setSidebarOpen(false);
    // Then navigate to the path
    navigate(path);
  };

  // Helper function to properly close the sidebar - simplified
  const closeSidebar = () => {
    // Only close if it's actually open
    if (sidebarOpen) {
      console.log('Closing sidebar');
      setSidebarOpen(false);
    } else {
      console.log('Sidebar already closed - no action needed');
    }
  };

  // Handle sidebar toggle - simplified
  const toggleSidebar = () => {
    console.log('Toggle sidebar, current state:', sidebarOpen);

    // Simply toggle the sidebar state
    const newState = !sidebarOpen;
    console.log('Setting sidebar to:', newState);
    setSidebarOpen(newState);
  };
  
  // Fetch dashboard data based on user role
  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!user) return;

      setDataLoading(true);
      try {
        let data = {};

        // Fetch role-specific data
        switch (user.role) {
          case 'student':
            try {
              const assignments = await api.get('/assignments/student');
              data.assignments = assignments.data;
            } catch (err) {
              console.error('Error fetching student assignments:', err);
              data.assignments = { success: false, error: 'Failed to load assignments' };
            }

            try {
              const attendance = await api.get('/attendance/student');
              data.attendance = attendance.data;
            } catch (err) {
              console.error('Error fetching student attendance:', err);
              data.attendance = { success: false, error: 'Failed to load attendance' };
            }
            break;

          case 'teacher':
            // TeacherDashboard fetches its own data via halaqaService.getHalaqaStudents()
            // No need to fetch students here as /students requires admin access

            try {
              const teacherAssignments = await api.get('/assignments/teacher');
              data.assignments = teacherAssignments.data;
            } catch (err) {
              console.error('Error fetching teacher assignments:', err);
              data.assignments = { success: false, error: 'Failed to load assignments' };
            }

            try {
              const teacherAttendance = await api.get('/attendance');
              data.attendance = teacherAttendance.data;
            } catch (err) {
              console.error('Error fetching teacher attendance:', err);
              data.attendance = { success: false, error: 'Failed to load attendance' };
            }
            break;

          case 'parent':
            try {
              const children = await api.get('/users/children');
              data.children = children.data;
            } catch (err) {
              console.error('Error fetching parent children:', err);
              data.children = { success: false, error: 'Failed to load children' };
            }
            break;

          case 'admin':
            try {
              const users = await api.get('/users');
              console.log('Fetched users:', users.data);
              data.users = users.data;
            } catch (err) {
              console.error('Error fetching users:', err);
              data.users = { success: false, error: 'Failed to load users' };
            }

            try {
              // Fetch halaqas with student counts
              const halaqas = await api.get('/halaqas');
              console.log('Fetched halaqas:', halaqas.data);
              
              // Fetch student counts for each halaqa
              const halaqasData = halaqas.data.data || halaqas.data;
              
              if (Array.isArray(halaqasData)) {
                // Fetch student counts for each halaqa in parallel
                const halaqasWithCounts = await Promise.all(
                  halaqasData.map(async (halaqa) => {
                    try {
                      const studentsResponse = await api.get(`/halaqas/${halaqa._id}/students`);
                      const studentsData = studentsResponse.data.data || studentsResponse.data;
                      
                      return {
                        ...halaqa,
                        studentCount: Array.isArray(studentsData) ? studentsData.length : 0
                      };
                    } catch (err) {
                      console.error(`Error fetching students for halaqa ${halaqa._id}:`, err);
                      return {
                        ...halaqa,
                        studentCount: 0
                      };
                    }
                  })
                );
                
                // Update the halaqas data with student counts
                data.halaqas = { 
                  ...halaqas.data,
                  data: halaqasWithCounts 
                };
              } else {
                data.halaqas = halaqas.data;
              }
            } catch (err) {
              console.error('Error fetching classes:', err);
              data.halaqas = { success: false, error: 'Failed to load classes' };
            }
            break;

          case 'super_admin':
            try {
              const centers = await api.get('/centers');
              data.centers = centers.data;
            } catch (err) {
              console.error('Error fetching centers:', err);
              data.centers = { success: false, error: 'Failed to load centers' };
            }

            try {
              const admins = await api.get('/users/admins');
              data.admins = admins.data;
            } catch (err) {
              console.error('Error fetching admins:', err);
              data.admins = { success: false, error: 'Failed to load admins' };
            }
            break;

          default:
            break;
        }

        setDashboardData(data);
        setError(null); // Clear any previous errors
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data. Please try again.');
      } finally {
        setDataLoading(false);
      }
    };

    if (user && isAuthenticated) {
      fetchDashboardData();
    }
  }, [user, isAuthenticated]);
  
  // Handle loading state
  if (loading || dataLoading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }
  
  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Render role-specific dashboard
  const renderDashboard = () => {
    if (!user) return null;
    
    switch (user.role) {
      case 'student':
        return <StudentDashboard data={dashboardData} />;
      
      case 'teacher':
        return <TeacherDashboard data={dashboardData} />;
      
      case 'parent':
        return <ParentDashboard data={dashboardData} user={user} />;
      
      case 'admin':
        return <AdminDashboard data={dashboardData} />;
      
      case 'super_admin':
        return <SuperAdminDashboard data={dashboardData} />;
      
      default:
        return (
          <div className="unknown-role">
            <h2>Welcome, {user.name}</h2>
            <p>Your role ({user.role}) doesn't have a specific dashboard yet.</p>
          </div>
        );
    }
  };
  
  // Handler to close sidebar when clicking outside
  const handleClickOutside = (e) => {
    // Make sure the sidebar is actually open before trying to close it
    if (sidebarOpen) {
      console.log('Closing sidebar from click outside');
      e.stopPropagation(); // Prevent event bubbling
      closeSidebar();
    }
  };

  return (
    <div className="dashboard-container">
      {/* Mobile Header removed - center branding header is used instead */}

      {/* Add overlay to capture clicks outside sidebar */}
      <div
        className={`sidebar-overlay ${sidebarOpen ? 'visible' : ''}`}
        onClick={handleClickOutside}
      />

      {/* Main Layout */}
      <div className="dashboard-layout">
        {/* Sidebar */}
        <RoleBasedSidebar isOpen={sidebarOpen} onClose={closeSidebar} />
        
        {/* Main Content */}
        <div className="dashboard-content">
          {error ? (
            <div className="error-message">{error}</div>
          ) : (
            renderDashboard()
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;